import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiMenu,
  FiX,
  FiPhone,
  FiMail,
  FiChevronDown
} from 'react-icons/fi';
import { Logo } from '../common/Logo';
import { Image } from '../ui/Image';
import { imageConfig } from '../../config/images';
import { companyStats } from '../../config/stats';

export const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown on scroll or outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        servicesButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !servicesButtonRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
        setActiveCategory(null);
      }
    };

    const handleScroll = () => {
      setActiveDropdown(null);
      setActiveCategory(null);
    };

    if (activeDropdown === 'services') {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [activeDropdown]);

  const servicesDropdown = {
    "SEO Services": [
      { name: "Traditional SEO", icon: "🔍", href: "/services/traditional-seo" },
      { name: "AI SEO", icon: "🤖", href: "/services/ai-seo" }
    ],
    "Paid Marketing": [
      { name: "Meta Ads", icon: "📱", href: "/services/meta-ads" },
      { name: "Google Ads", icon: "🎯", href: "/services/google-ads" },
      { name: "Bing Ads", icon: "🔎", href: "/services/bing-ads" },
      { name: "YouTube Ads", icon: "📺", href: "/services/youtube-ads" }
    ],
    "IT Services": [
      { name: "Custom Web Development", icon: "💻", href: "/services/custom-web-development" },
      { name: "WordPress", icon: "🌐", href: "/services/wordpress" },
      { name: "Shopify", icon: "🛍️", href: "/services/shopify" },
      { name: "App Development", icon: "📱", href: "/services/app-development" },
      { name: "WhatsApp API", icon: "💬", href: "/services/whatsapp-api" }
    ],
    "Social Media Marketing": [
      { name: "Facebook Marketing", icon: "👥", href: "/services/facebook-marketing" },
      { name: "Instagram Marketing", icon: "📷", href: "/services/instagram-marketing" },
      { name: "YouTube Marketing", icon: "🎬", href: "/services/youtube-marketing" },
      { name: "Pinterest Marketing", icon: "📌", href: "/services/pinterest-marketing" },
      { name: "LinkedIn Marketing", icon: "💼", href: "/services/linkedin-marketing" },
      { name: "WhatsApp Marketing", icon: "💬", href: "/services/whatsapp-marketing" },
      { name: "TikTok Marketing", icon: "🎵", href: "/services/tiktok-marketing" },
      { name: "Twitter Marketing", icon: "🐦", href: "/services/twitter-marketing" },
      { name: "Threads Marketing", icon: "🧵", href: "/services/threads-marketing" }
    ],
    "Other Services": [
      { name: "Digital Marketing Course", icon: "📚", href: "/services/digital-marketing-course" },
      { name: "Digital Marketing Consulting", icon: "💡", href: "/services/digital-marketing-consulting" },
      { name: "Business Consulting", icon: "📊", href: "/services/business-consulting" },
      { name: "Affiliate Program", icon: "🤝", href: "/services/affiliate-program" },
      { name: "White Label Digital Marketing", icon: "🏷️", href: "/services/white-label" },
      { name: "E-Commerce Registration & Management", icon: "🛒", href: "/services/ecommerce-registration" },
      { name: "Quick Commerce Registration & Management", icon: "⚡", href: "/services/quick-commerce" },
      { name: "AI Consulting", icon: "🤖", href: "/services/ai-consulting" }
    ]
  };

  const revenueDropdown = {
    "Digital Intelligence": [
      { name: "Web Channel Call Tracking", icon: "📞" },
      { name: "SEO Reporting & Forecasting", icon: "📊" },
      { name: "Channel Attribution & Forecasting", icon: "🎯" },
      { name: "Digital Marketing Competitor Analysis", icon: "🔍" },
      { name: "Private Equity Due Diligence", icon: "💼" },
      { name: "Revenue Operations", icon: "⚙️" }
    ],
    "Conversion": [
      { name: "Conversion Rate Optimization", icon: "📈" },
      { name: "User Experience Testing", icon: "🧪" },
      { name: "Landing Pages & Funnels", icon: "🎯" },
      { name: "Online Review Acceleration", icon: "⭐" },
      { name: "Channel Partner Sales Pipeline", icon: "🤝" },
      { name: "Website Personalization", icon: "👤" }
    ],
    "Marketing Automation": [
      { name: "Account-Based Marketing Services", icon: "🎯" },
      { name: "Lead Nurture Email Marketing", icon: "📧" },
      { name: "Email Marketing Management", icon: "✉️" },
      { name: "Salesforce Marketing Automation", icon: "☁️" },
      { name: "Microsoft Dynamics Marketing", icon: "🔷" },
      { name: "Employment & Recruiting Marketing", icon: "👥" }
    ],
    "Commerce Platforms": [
      { name: "SEO for Amazon", icon: "📦" },
      { name: "Advertising Management for Amazon", icon: "🛒" },
      { name: "Shopify Optimization Services", icon: "🛍️" },
      { name: "Facebook Marketplace for Business", icon: "🏪" },
      { name: "Influencer Marketing Services", icon: "👑" },
      { name: "Walmart Marketplace Advertising", icon: "🏬" }
    ]
  };

  const uxAiDropdown = {
    "Design": [
      { name: "Website Design", icon: "🎨" },
      { name: "Website Redesign", icon: "🔄" },
      { name: "Rapid Web Design", icon: "⚡" },
      { name: "Social Media Design", icon: "📱" },
      { name: "Ecommerce Website Design", icon: "🛒" },
      { name: "Email Marketing Design", icon: "📧" }
    ],
    "AI Services": [
      { name: "AI Consulting", icon: "🤖" },
      { name: "AI Digital Marketing Services", icon: "🚀" },
      { name: "ChatGPT Optimization", icon: "💬" },
      { name: "AI Agent Development", icon: "🤖" },
      { name: "AI & GPT Integration", icon: "🔗" },
      { name: "Enterprise GEO Services", icon: "🌐" }
    ],
    "Development": [
      { name: "Digital Experience Development", icon: "💻" },
      { name: "WordPress Development", icon: "🌐" },
      { name: "Shopify Ecommerce Development", icon: "🛍️" },
      { name: "Web Infrastructure & Maintenance", icon: "🔧" },
      { name: "Content Management Systems", icon: "📝" },
      { name: "ADA Compliance Services", icon: "♿" }
    ],
    "Challenges We Solve": [
      { name: "My Website Doesn't Drive Leads", icon: "🎯" },
      { name: "My Website Traffic Is Going Down", icon: "📉" },
      { name: "My Website Doesn't Convert", icon: "🔄" },
      { name: "My Website Isn't Driving ROI", icon: "💰" },
      { name: "My Website Isn't Showing Up", icon: "🔍" },
      { name: "My Agency Isn't Driving Results", icon: "📊" }
    ]
  };

  const revenuePlatformDropdown = [
    { name: "Revenue Acceleration Platform", icon: "🚀" },
    { name: "RevenueCloudFX Revenue Platform", icon: "☁️" },
    { name: "CRM Integrations", icon: "🔗" },
    { name: "Customer Data Platform (CDP)", icon: "📊" },
    { name: "Lead Management & Scheduling", icon: "📅" },
    { name: "CRM Retargeting Technology", icon: "🎯" },
    { name: "Contact Enrichment", icon: "👥" }
  ];

  const aboutDropdown = {
    "Who We Are": [
      { name: "Our Approach", icon: "🎯" },
      { name: "About Us", icon: "👥" },
      { name: "Careers (We Are Hiring!)", icon: "💼" },
      { name: "Contact Info", icon: "📞" },
      { name: "Mirani Marketing Reviews", icon: "⭐" },
      { name: "Awards", icon: "🏆" }
    ],
    "Community Impact": [
      { name: "Company Values", icon: "💎" },
      { name: "Community Impact", icon: "🌍" },
      { name: "Mirani Builds", icon: "🏗️" },
      { name: "Equality at Mirani Marketing", icon: "🤝" },
      { name: "STEM Program", icon: "🔬" },
      { name: "Company History", icon: "📚" }
    ],
    "Pricing Guides": [
      { name: "How Much Should a Website Cost?", icon: "💰" },
      { name: "How Much Does PPC Cost?", icon: "💸" },
      { name: "Google Ads Cost", icon: "🎯" },
      { name: "Social Media Pricing", icon: "📱" },
      { name: "How Much Does SEO Cost?", icon: "🔍" },
      { name: "Local SEO Pricing", icon: "📍" }
    ],
    "Content Library": [
      { name: "Our Blog", icon: "📝" },
      { name: "SEO Checker Report", icon: "📊" },
      { name: "Free Keyword Suggestion Tool", icon: "🔍" },
      { name: "Fix Your Funnel", icon: "🔧" },
      { name: "FAQFox", icon: "❓" },
      { name: "View All Tools", icon: "🛠️" }
    ]
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-lg">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-2 px-4 md:px-6 lg:px-12 xl:px-16">
        <div className="w-full flex justify-between items-center text-xs md:text-sm">
          <div className="flex items-center space-x-2 md:space-x-6 overflow-hidden">
            <a href="mailto:contact@miranimarketing.com" className="hidden sm:flex items-center space-x-1 md:space-x-2 hover:text-yellow-300 transition-colors">
              <FiMail className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
              <span className="truncate">contact@miranimarketing.com</span>
            </a>
            <a href="tel:+919313258355" className="flex items-center space-x-1 md:space-x-2 hover:text-yellow-300 transition-colors">
              <FiPhone className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
              <span>+91-9313258355</span>
            </a>
            <a href="tel:+919624361042" className="hidden md:flex items-center space-x-1 md:space-x-2 hover:text-yellow-300 transition-colors">
              <FiPhone className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
              <span>+91-9624361042</span>
            </a>
          </div>
          <Link to="/contact" className="hidden md:flex items-center space-x-2 text-xs lg:text-sm hover:text-yellow-300 transition-colors cursor-pointer">
            <span>🚀</span>
            <span className="whitespace-nowrap">Get Your Free Digital Marketing Consultation</span>
          </Link>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="w-full px-2 md:px-4 lg:px-6 xl:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <Logo size="large" />
            <div className="ml-2 md:ml-3 hidden md:block">
              <div className="text-xs md:text-sm text-gray-600 leading-tight">Digital Marketing</div>
              <div className="text-[10px] md:text-xs text-gray-500">That Drives Revenue®</div>
            </div>
          </Link>

          {/* Revenue Stats */}
          <div className="hidden xl:flex items-center text-blue-600 font-semibold text-sm">
            <span className="mr-2">📈 TOTAL AD SPEND TILL NOW</span>
            <div className="ml-2 text-xl font-bold">$10,00,000+</div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <FiX className="h-6 w-6" />
              ) : (
                <FiMenu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Main Menu - Desktop Only */}
        <div className="hidden lg:flex justify-center items-center py-3 border-t border-gray-100 overflow-x-auto gap-0.5 xl:gap-1 scrollbar-hide">
          {/* Home */}
          <Link
            to="/"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Home
          </Link>

          {/* About Us */}
          <Link
            to="/about"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            About Us
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative hidden lg:block"
            ref={dropdownRef}
          >
            <button
              ref={servicesButtonRef}
              onClick={() => setActiveDropdown(activeDropdown === 'services' ? null : 'services')}
              className="flex items-center px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
            >
              Services
              <FiChevronDown className={`ml-1.5 h-4 w-4 transition-transform duration-300 ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
            </button>

            {activeDropdown === 'services' && (
              <div
                className="fixed left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50 max-h-[80vh] overflow-y-auto w-[95vw] max-w-[1100px]"
              >
                {/* Orange Header Row with Main Categories */}
                <div className="grid grid-cols-5 bg-gradient-to-r from-orange-500 to-orange-600 sticky top-0 z-10">
                  <div className="px-2 xl:px-3 py-2 text-white font-bold text-[9px] xl:text-[10px] text-center border-r border-orange-400">
                    SEO SERVICES
                  </div>
                  <div className="px-2 xl:px-3 py-2 text-white font-bold text-[9px] xl:text-[10px] text-center border-r border-orange-400">
                    PAID MARKETING
                  </div>
                  <div className="px-2 xl:px-3 py-2 text-white font-bold text-[9px] xl:text-[10px] text-center border-r border-orange-400">
                    IT SERVICES
                  </div>
                  <div className="px-2 xl:px-3 py-2 text-white font-bold text-[9px] xl:text-[10px] text-center border-r border-orange-400">
                    SOCIAL MEDIA MARKETING
                  </div>
                  <div className="px-2 xl:px-3 py-2 text-white font-bold text-[9px] xl:text-[10px] text-center">
                    OTHER SERVICES
                  </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-5">
                  {/* SEO Services Column */}
                  <div className="border-r border-gray-300 p-1.5 xl:p-2 bg-gray-50">
                    <ul className="space-y-1">
                      <li>
                        <Link 
                          to="/services/traditional-seo"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🔍</span>
                          <span className="leading-tight">TRADITIONAL SEO</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/ai-seo"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🤖</span>
                          <span className="leading-tight">AI SEO</span>
                        </Link>
                      </li>
                    </ul>
                  </div>

                  {/* Paid Marketing Column */}
                  <div className="border-r border-gray-300 p-1.5 xl:p-2 bg-gray-50">
                    <ul className="space-y-1">
                      <li>
                        <Link 
                          to="/services/meta-ads"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">📱</span>
                          <span className="leading-tight">META ADS</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/google-ads"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🎯</span>
                          <span className="leading-tight">GOOGLE ADS</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/bing-ads"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🔎</span>
                          <span className="leading-tight">BING ADS</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/youtube-ads"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">📺</span>
                          <span className="leading-tight">YOUTUBE ADS</span>
                        </Link>
                      </li>
                    </ul>
                  </div>

                  {/* IT Services Column */}
                  <div className="border-r border-gray-300 p-1.5 xl:p-2 bg-gray-50">
                    <ul className="space-y-1">
                      <li>
                        <Link 
                          to="/services/custom-web-development"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">💻</span>
                          <span className="leading-tight">CUSTOM WEB DEVELOPMENT</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/wordpress"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🌐</span>
                          <span className="leading-tight">WORDPRESS</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/shopify"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">🛍️</span>
                          <span className="leading-tight">SHOPIFY</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/app-development"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-xs xl:text-sm flex-shrink-0">📱</span>
                          <span className="leading-tight">APP DEVELOPMENT</span>
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/services/whatsapp-api"
                          className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <span className="text-sm flex-shrink-0">💬</span>
                          <span className="leading-tight">WHATSAPP API</span>
                        </Link>
                      </li>
                    </ul>
                  </div>

                  {/* Social Media Marketing Column */}
                  <div className="border-r border-gray-300 p-1.5 xl:p-2 bg-gray-50">
                    <ul className="space-y-1">
                      {[
                        { to: "/services/facebook-marketing", icon: "👥", text: "FACEBOOK MARKETING" },
                        { to: "/services/instagram-marketing", icon: "📷", text: "INSTAGRAM MARKETING" },
                        { to: "/services/youtube-marketing", icon: "🎬", text: "YOUTUBE MARKETING" },
                        { to: "/services/pinterest-marketing", icon: "📌", text: "PINTEREST MARKETING" },
                        { to: "/services/linkedin-marketing", icon: "💼", text: "LINKEDIN MARKETING" },
                        { to: "/services/whatsapp-marketing", icon: "💬", text: "WHATSAPP MARKETING" },
                        { to: "/services/tiktok-marketing", icon: "🎵", text: "TIKTOK MARKETING" },
                        { to: "/services/twitter-marketing", icon: "🐦", text: "TWITTER MARKETING" },
                        { to: "/services/threads-marketing", icon: "🧵", text: "THREADS" }
                      ].map((item, idx) => (
                        <li key={idx}>
                          <Link 
                            to={item.to}
                            className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="text-xs xl:text-sm flex-shrink-0">{item.icon}</span>
                            <span className="leading-tight">{item.text}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Other Services Column */}
                  <div className="p-1.5 xl:p-2 bg-gray-50">
                    <ul className="space-y-1">
                      {[
                        { to: "/services/digital-marketing-course", icon: "📚", text: "DIGITAL MARKETING COURSE" },
                        { to: "/services/digital-marketing-consulting", icon: "💡", text: "DIGITAL MARKETING CONSULTING" },
                        { to: "/services/business-consulting", icon: "📊", text: "BUSINESS CONSULTING" },
                        { to: "/services/affiliate-program", icon: "🤝", text: "AFFILIATE PROGRAM" },
                        { to: "/services/white-label-digital-marketing", icon: "🏷️", text: "WHITE LABEL DIGITAL MARKETING" },
                        { to: "/services/ecommerce-registration", icon: "🛒", text: "E-COMMERCE REGISTRATION & MGMT" },
                        { to: "/services/quick-commerce-registration", icon: "⚡", text: "QUICK COMMERCE REGISTRATION & MGMT" },
                        { to: "/services/ai-consulting", icon: "🤖", text: "AI CONSULTING" }
                      ].map((item, idx) => (
                        <li key={idx}>
                          <Link 
                            to={item.to}
                            className="flex items-center space-x-1 xl:space-x-1.5 p-1 xl:p-1.5 rounded bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-[9px] xl:text-[10px] font-medium transition-all border border-gray-200 hover:border-orange-300 shadow-sm"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="text-xs xl:text-sm flex-shrink-0">{item.icon}</span>
                            <span className="leading-tight">{item.text}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pricing */}
          <Link
            to="/pricing"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Pricing
          </Link>

          {/* Blog */}
          <Link
            to="/blog"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Blog
          </Link>

          {/* Courses */}
          <Link
            to="/courses"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Courses
          </Link>

          {/* White Label */}
          <Link
            to="/white-label"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            White Label
          </Link>

          {/* Affiliate Programme */}
          <Link
            to="/affiliate"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Affiliate Program
          </Link>

          {/* Business Consulting */}
          <Link
            to="/business-consulting"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Business Consulting
          </Link>

          {/* Careers */}
          <Link
            to="/careers"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Careers
          </Link>

          {/* Contact Us */}
          <Link
            to="/contact"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            Contact Us
          </Link>

          {/* White Paper */}
          <Link
            to="/white-paper"
            className="px-2.5 py-2 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 font-medium text-xs xl:text-sm transition-all duration-300 hover:shadow-md whitespace-nowrap"
          >
            White Paper
          </Link>

          {/* Get a Proposal Button */}
          <Link
            to="/contact"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-3 xl:px-4 py-2 rounded-full font-semibold text-xs xl:text-sm transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex-shrink-0"
          >
            Get a Proposal
          </Link>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            <Link 
              to="/" 
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            <Link 
              to="/about" 
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About Us
            </Link>
            
            {/* Services Accordion */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between px-3 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white font-semibold transition-colors"
              >
                <span>Services</span>
                <FiChevronDown className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileServicesOpen && (
                <div className="bg-gray-50 p-2 space-y-3">
                  {/* SEO Services */}
                  <div>
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">SEO SERVICES</div>
                    <div className="mt-1 space-y-1">
                      <Link to="/services/traditional-seo" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🔍 Traditional SEO
                      </Link>
                      <Link to="/services/ai-seo" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🤖 AI SEO
                      </Link>
                    </div>
                  </div>
                  
                  {/* Paid Marketing */}
                  <div>
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">PAID MARKETING</div>
                    <div className="mt-1 space-y-1">
                      <Link to="/services/meta-ads" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📱 Meta Ads
                      </Link>
                      <Link to="/services/google-ads" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                       🎯 Google Ads
                      </Link>
                      <Link to="/services/bing-ads" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🔎 Bing Ads
                      </Link>
                      <Link to="/services/youtube-ads" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📺 YouTube Ads
                      </Link>
                    </div>
                  </div>
                  
                  {/* IT Services */}
                  <div>
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">IT SERVICES</div>
                    <div className="mt-1 space-y-1">
                      <Link to="/services/custom-web-development" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        💻 Custom Web Development
                      </Link>
                      <Link to="/services/wordpress" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🌐 WordPress
                      </Link>
                      <Link to="/services/shopify" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🛍️ Shopify
                      </Link>
                      <Link to="/services/app-development" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📱 App Development
                      </Link>
                      <Link to="/services/whatsapp-api" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        💬 WhatsApp API
                      </Link>
                    </div>
                  </div>
                  
                  {/* Social Media Marketing */}
                  <div>
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">SOCIAL MEDIA MARKETING</div>
                    <div className="mt-1 space-y-1">
                      <Link to="/services/facebook-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        👥 Facebook Marketing
                      </Link>
                      <Link to="/services/instagram-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📷 Instagram Marketing
                      </Link>
                      <Link to="/services/youtube-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🎬 YouTube Marketing
                      </Link>
                      <Link to="/services/pinterest-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📌 Pinterest Marketing
                      </Link>
                      <Link to="/services/linkedin-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        💼 LinkedIn Marketing
                      </Link>
                      <Link to="/services/whatsapp-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        💬 WhatsApp Marketing
                      </Link>
                      <Link to="/services/tiktok-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🎵 TikTok Marketing
                      </Link>
                      <Link to="/services/twitter-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🐦 Twitter Marketing
                      </Link>
                      <Link to="/services/threads-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🧵 Threads Marketing
                      </Link>
                    </div>
                  </div>
                  
                  {/* Other Services */}
                  <div>
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded">OTHER SERVICES</div>
                    <div className="mt-1 space-y-1">
                      <Link to="/services/digital-marketing-course" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📚 Digital Marketing Course
                      </Link>
                      <Link to="/services/digital-marketing-consulting" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        💡 Digital Marketing Consulting
                      </Link>
                      <Link to="/services/business-consulting" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        📊 Business Consulting
                      </Link>
                      <Link to="/services/affiliate-program" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🤝 Affiliate Program
                      </Link>
                      <Link to="/services/white-label-digital-marketing" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🏷️ White Label Digital Marketing
                      </Link>
                      <Link to="/services/ecommerce-registration" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🛒 E-Commerce Registration & Management
                      </Link>
                      <Link to="/services/quick-commerce-registration" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        ⚡ Quick Commerce Registration & Management
                      </Link>
                      <Link to="/services/ai-consulting" className="block px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-orange-600 rounded" onClick={() => setIsMobileMenuOpen(false)}>
                        🤖 AI Consulting
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/pricing"
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Pricing
            </Link>

            <Link
              to="/blog"
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>
            
            <Link
              to="/careers"
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Careers
            </Link>

            <Link
              to="/contact"
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact Us
            </Link>

            <Link
              to="/white-paper"
              className="block px-3 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              White Paper
            </Link>

            <Link
              to="/contact"
              className="block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg mt-4 font-semibold text-center shadow-lg hover:shadow-xl transition-shadow"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get a Proposal
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};